import { GridStack } from "gridstack";
import "gridstack/dist/h5/gridstack-dd-native";
import { DEFAUL_GRID_COLUMNS } from "../../constants/grid-stack-options"; 

export default class LayoutTemplatesGrid {

    __$elementContainer = null
    templates = []
    gridInstance = null;
    options = {}
    
    constructor($elementContainer, options = {}) {
        this.__$elementContainer = $elementContainer
        this.__initOption(options)
        this.gridInstance = GridStack.init({
                column: DEFAUL_GRID_COLUMNS,
                ...(options.grid || {})
            },
            this.__$elementContainer.get(0)
        );
    }

    __initOption(options) {
        this.options = options
        this.templates = options?.templates || this.templates
    }

    __getMaxWidthAndHeightOfTemplate() {
        return this.templates.reduce(
            (acc, template) => {
                acc.maxWidth = acc.maxWidth < template.width ? template.width : acc.maxWidth;
                acc.maxHeight = acc.maxHeight < template.height ? template.height : acc.maxHeight;

                return acc;
            },
            { maxWidth: 0, maxHeight: 0 }
        )
    }

    __calculateSizeWidget(width, height) {
        const { maxWidth, maxHeight } = this.__getMaxWidthAndHeightOfTemplate()
        const maxSize = maxWidth >= maxHeight ? maxWidth : maxHeight;
        
        const w = Math.ceil(width * DEFAUL_GRID_COLUMNS / maxSize);
        const h = Math.ceil(height * DEFAUL_GRID_COLUMNS / maxSize);

        return { w, h }
    }

    __calculatePositionTopWidget() {
        return this.gridInstance.save().reduce(
            (acc, gridData) => {
                const bottonPos = gridData.y + gridData.h;
                return acc < bottonPos ? bottonPos : acc
            },
            0
        )
    }

    load() {
        const widgets = this.templates.map(({ instanceId, content, width, height, gridData }) => {
            const { w, h } = this.__calculateSizeWidget(width, height)

            return {
                id: instanceId,
                w,
                h,
                minW: w,
                minH: h,
                ...(gridData || {}),
                content
            }
        })

        this.gridInstance.load(widgets)
    }

    addWidget(templateData) {
        const widet = {
            id: templateData.instanceId,
            x: 0,
            y: this.__calculatePositionTopWidget(),
            content: templateData.content
        }

        this.gridInstance.addWidget(widet)
    }

    add(templateData) {
        this.templates.push(templateData);
        this.addWidget(templateData)
        this.load()
    }

    remove(instanceId) {
        this.templates = this.templates.filter(
            templateData => templateData.instanceId !== instanceId
        )
        this.load();
    }
    
    applyNewTemplatesOrder(newTemplatesOrder) {
        if (!newTemplatesOrder || typeof newTemplatesOrder !== 'object') {
            return;
        }

        // 1) Build the desired order of instanceIds from mapping index => instanceId
        const orderedIds = Object.entries(newTemplatesOrder)
            .sort(([a], [b]) => parseInt(a) - parseInt(b))
            .map(([, instanceId]) => instanceId);

        // 2) Append any templates not specified in newTemplatesOrder, preserving current order
        const currentIds = this.templates.map(t => t.instanceId);
        const unspecifiedIds = currentIds.filter(id => !orderedIds.includes(id));
        const finalIdsOrder = [...orderedIds, ...unspecifiedIds];

        // 3) Reorder the templates array to match the finalIdsOrder
        const idToTemplate = {};
        this.templates.forEach(t => { idToTemplate[t.instanceId] = t });
        this.templates = finalIdsOrder.map(id => idToTemplate[id]).filter(Boolean);

        // 4) Rebuild grid nodes in the same order, stacking vertically so save() reflects ordering
        const savedNodes = this.gridInstance.save();
        const idToNode = {};
        savedNodes.forEach(n => { idToNode[n.id] = n });

        let nextY = 0;
        const nodes = finalIdsOrder.map(id => {
            const template = idToTemplate[id];
            if (!template) return null;

            const existing = idToNode[id];
            let w, h;
            if (existing) {
                w = existing.w;
                h = existing.h;
            } else {
                const size = this.__calculateSizeWidget(template.width, template.height);
                w = size.w;
                h = size.h;
            }

            const node = {
                id,
                w,
                h,
                minW: w,
                minH: h,
                x: 0,
                y: nextY,
                content: template.content
            };
            nextY += h;
            return node;
        }).filter(Boolean);

        // 5) Reset the grid with the newly ordered nodes
        this.gridInstance.removeAll();
        this.gridInstance.load(nodes);
    }

    getData() {
        const originalYValues = this.templates.map(template => template.gridData?.y || 0);
        const gridData = this.gridInstance.save();
        //we need set origin y value to be correct order after save, this important when pagination
        const gridDataWithRemapYValue = gridData.map((gridItem, index) => {
          gridItem.y = originalYValues[index];
          return gridItem;
        });

        return gridDataWithRemapYValue.map(({id, w, h, x, y }) => {
            const templateData = this.templates.find(item => item.instanceId === id);

            return {
                instance_id: templateData.instanceId,
                template_id: templateData.id,
                width: templateData.width,
                height: templateData.height,
                is_custom: templateData.is_custom,
                name: templateData.name,
                theme: templateData.theme,
                customer_id: templateData.customer_id,
                saveAsNew: templateData.saveAsNew,
                w,
                h,
                x,
                y
            }
        })
    }

    applyGroupingResult({ removedInstanceIds = [], updatedTemplates = {}, layoutSettings = [] } = {}) {
        if (Array.isArray(removedInstanceIds) && removedInstanceIds.length) {
            const removalSet = new Set(removedInstanceIds);
            const remaining = this.templates.filter(templateData => !removalSet.has(templateData.instanceId));
            this.templates.length = 0;
            Array.prototype.push.apply(this.templates, remaining);
        }

        if (updatedTemplates && typeof updatedTemplates === 'object') {
            Object.values(updatedTemplates).forEach((templateUpdate = {}) => {
                const { instance_id: instanceId, height } = templateUpdate;
                if (!instanceId) {
                    return;
                }

                const template = this.templates.find(item => item.instanceId === instanceId);
                if (!template) {
                    return;
                }

                if (typeof height === 'number') {
                    template.height = height;
                    if (template.gridData) {
                        const size = this.__calculateSizeWidget(template.width, template.height);
                        template.gridData.h = size.h;
                        template.gridData.minH = size.h;
                    }
                }
            });
        }

        if (Array.isArray(layoutSettings) && layoutSettings.length) {
            const orderMap = {};
            layoutSettings.forEach((item, index) => {
                if (item && item.instance_id) {
                    orderMap[index] = item.instance_id;
                }
            });

            this.applyNewTemplatesOrder(orderMap);
        } else {
            this.load();
        }
    }
}
